import { inject, Injectable } from '@angular/core';
import { Store } from '@sama/app-state';
import { assert } from '@sama/js-core';
import { filter } from 'lodash';
import { map } from 'rxjs';
import {
  AnnotationsObjectState,
  isAnnotationsObject,
  isItemObject,
  isContextObject,
  isTextObjectState,
  ObjectState,
  ContextObjectState,
  TextObjectState,
} from '../../../cross-team-shared/annotation/shared/state/object-state';
import { assertDefined } from '../../../cross-team-shared/misc-utils/strict-record';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';

@Injectable()
export class ItemService {
  private readonly store = inject(TaskWorkspaceStore);

  getItems$() {
    return this.store('objects').$.pipe(
      map((objects) =>
        filter(objects, (object) => {
          assertDefined(object);
          return isItemObject(object);
        }),
      ),
    );
  }

  getContextStoresForItem(itemId: number) {
    const contextObjectStore = this.getContextObjectStoreForItem(itemId);

    return contextObjectStore.state().childObjectIds.map((childObjectId) => {
      const childObjectStore = this.store.getObjectStore(childObjectId);
      assert(
        isTextObjectStore(childObjectStore),
        'Context object must be a TextObject',
      );
      return childObjectStore;
    });
  }

  getAnnotationStoresForItem$(itemId: number) {
    const annotationsObjectStore =
      this.getAnnotationsObjectStoreForItem(itemId);

    return annotationsObjectStore
      .state()
      .childObjectIds.map((childObjectId) =>
        this.store.getObjectStore(childObjectId),
      );
  }

  private getContextObjectStoreForItem(itemId: number) {
    const item = this.store.getObjectStore(itemId).state();
    const contextStore = item.childObjectIds
      .map((childObjectId) => this.store.getObjectStore(childObjectId))
      .find(isContextObjectStore);
    assertDefined(contextStore);
    return contextStore;
  }

  private getAnnotationsObjectStoreForItem(itemId: number) {
    const item = this.store.getObjectStore(itemId).state();
    const annotationsObject = item.childObjectIds
      .map((childObjectId) => this.store.getObjectStore(childObjectId))
      .find(isAnnotationsObjectStore);
    assertDefined(annotationsObject);
    return annotationsObject;
  }
}

function isContextObjectStore(
  store: Store<ObjectState>,
): store is Store<ContextObjectState> {
  return isContextObject(store.state());
}

function isAnnotationsObjectStore(
  store: Store<ObjectState>,
): store is Store<AnnotationsObjectState> {
  return isAnnotationsObject(store.state());
}

function isTextObjectStore(
  store: Store<ObjectState>,
): store is Store<TextObjectState> {
  return isTextObjectState(store.state());
}
